import React, { useRef, useState } from 'react';
import useSWR from 'swr';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import api from '../../../services/api';
import { fetcher } from '../../../services/fetcher';

const MONTH_FIELDS = [
  { key: 'newContractMonths', label: 'NC.Coe', fullName: 'New Crew COE months' },
  { key: 'newExtensionMonths', label: 'NC.Ext.Coe', fullName: 'New Crew extended COE months' },
  { key: 'exContractMonths', label: 'XC.Coe', fullName: 'x-crew COE months' },
  { key: 'exExtensionMonths', label: 'XC.Ext.Coe', fullName: 'x-crew extended COE months' },
] as const;

type MonthKey = (typeof MONTH_FIELDS)[number]['key'];
type MonthStrings = Record<MonthKey, string>;
type MonthValues = Record<MonthKey, number | null>;

type RankColumn = {
  id: number;
  code?: string | null;
  name: string;
  abrv?: string | null;
  sortOrder: number;
};

type NationalityColumn = {
  id: number;
  code?: string | null;
  name: string;
  sortOrder: number;
};

type TermRow = MonthValues & {
  rankId: number;
  nationalityId: number;
};

type MatrixResponse = {
  ranks: RankColumn[];
  nationalities: NationalityColumn[];
  terms: TermRow[];
};

const EMPTY_MONTHS: MonthStrings = {
  newContractMonths: '',
  newExtensionMonths: '',
  exContractMonths: '',
  exExtensionMonths: '',
};

function cellKey(rankId: number, nationalityId: number) {
  return `${rankId}:${nationalityId}`;
}

function toStrings(term?: MonthValues | null): MonthStrings {
  if (!term) return { ...EMPTY_MONTHS };
  return {
    newContractMonths: term.newContractMonths == null ? '' : String(term.newContractMonths),
    newExtensionMonths: term.newExtensionMonths == null ? '' : String(term.newExtensionMonths),
    exContractMonths: term.exContractMonths == null ? '' : String(term.exContractMonths),
    exExtensionMonths: term.exExtensionMonths == null ? '' : String(term.exExtensionMonths),
  };
}

function parseBand(raw: MonthStrings): MonthValues | 'invalid' {
  const parsed = {} as MonthValues;
  for (const field of MONTH_FIELDS) {
    const text = raw[field.key].trim();
    if (!text) {
      parsed[field.key] = null;
      continue;
    }
    if (!/^\d+$/.test(text)) return 'invalid';
    const value = Number(text);
    if (value > 60) return 'invalid';
    parsed[field.key] = value;
  }
  return parsed;
}

function sameMonths(a: MonthValues, b: MonthValues) {
  return MONTH_FIELDS.every((field) => a[field.key] === b[field.key]);
}

function apiMessage(error: unknown, fallback: string) {
  const msg = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string' && msg.trim()) return msg;
  return error instanceof Error && error.message ? error.message : fallback;
}

const ContractTermsPanel: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const { showError } = useNotification();
  const canEdit = can('crew_parameters', 'edit');

  const { data, error, isLoading, mutate } = useSWR<MatrixResponse>(
    '/crew-contract-terms/matrix',
    fetcher,
    { revalidateOnFocus: false },
  );

  const [edits, setEdits] = useState<Record<string, MonthStrings>>({});
  const [pending, setPending] = useState<Set<string>>(new Set());
  const editsRef = useRef(edits);
  const saveGen = useRef<Record<string, number>>({});

  const savedByKey = new Map<string, MonthValues>();
  for (const term of data?.terms ?? []) {
    savedByKey.set(cellKey(term.rankId, term.nationalityId), {
      newContractMonths: term.newContractMonths,
      newExtensionMonths: term.newExtensionMonths,
      exContractMonths: term.exContractMonths,
      exExtensionMonths: term.exExtensionMonths,
    });
  }

  const displayValue = (rankId: number, nationalityId: number, field: MonthKey) => {
    const key = cellKey(rankId, nationalityId);
    const edited = edits[key];
    if (edited) return edited[field];
    return toStrings(savedByKey.get(key))[field];
  };

  const onChange = (rankId: number, nationalityId: number, field: MonthKey, value: string) => {
    if (value !== '' && !/^\d{1,2}$/.test(value)) return;
    if (value !== '' && Number(value) > 60) return;
    const key = cellKey(rankId, nationalityId);
    const base = editsRef.current[key] ?? toStrings(savedByKey.get(key));
    const next = { ...editsRef.current, [key]: { ...base, [field]: value } };
    editsRef.current = next;
    setEdits(next);
  };

  const saveCell = async (rankId: number, nationalityId: number) => {
    const key = cellKey(rankId, nationalityId);
    const snapshot = editsRef.current[key];
    if (!snapshot) return;

    const parsed = parseBand(snapshot);
    if (parsed === 'invalid') {
      showError('Enter a whole number from 0 to 60, or leave the cell empty.');
      const next = { ...editsRef.current };
      delete next[key];
      editsRef.current = next;
      setEdits(next);
      return;
    }

    const saved = savedByKey.get(key) ?? {
      newContractMonths: null,
      newExtensionMonths: null,
      exContractMonths: null,
      exExtensionMonths: null,
    };
    if (sameMonths(parsed, saved)) {
      const next = { ...editsRef.current };
      delete next[key];
      editsRef.current = next;
      setEdits(next);
      return;
    }

    const version = (saveGen.current[key] ?? 0) + 1;
    saveGen.current[key] = version;
    setPending((current) => new Set(current).add(key));
    try {
      await api.put('/crew-contract-terms/cell', {
        rankId,
        nationalityId,
        ...parsed,
      });
      if (saveGen.current[key] !== version) return;
      setEdits((current) => {
        const latest = current[key];
        const latestParsed = latest ? parseBand(latest) : null;
        const stillMatches =
          latestParsed == null ||
          (latestParsed !== 'invalid' && sameMonths(latestParsed, parsed));
        if (!stillMatches) {
          editsRef.current = current;
          return current;
        }
        const next = { ...current };
        delete next[key];
        editsRef.current = next;
        return next;
      });
      await mutate();
    } catch (saveError: unknown) {
      if (saveGen.current[key] !== version) return;
      const next = { ...editsRef.current };
      delete next[key];
      editsRef.current = next;
      setEdits(next);
      showError(apiMessage(saveError, 'Could not save contract months.'));
    } finally {
      if (saveGen.current[key] === version) {
        setPending((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    }
  };

  if (isLoading && !data) {
    return <p className={cn('p-5 text-sm', colors.textMuted)}>Loading contract terms…</p>;
  }

  if (error && !data) {
    return (
      <p className="p-5 text-sm text-red-700">
        {apiMessage(error, 'Could not load contract terms.')}
      </p>
    );
  }

  const ranks = data?.ranks ?? [];
  const nationalities = data?.nationalities ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={cn('flex shrink-0 items-center justify-between gap-3 px-5 pt-4 pb-3')}>
        <p className={cn('text-sm', colors.textMuted)}>
          Type the months in the grid. Leaving a cell saves that rank and nationality. Clear all four
          months to remove them.
        </p>
        {pending.size > 0 ? (
          <span className={cn('shrink-0 text-xs', colors.textMuted)}>Saving…</span>
        ) : null}
      </div>

      {ranks.length === 0 ? (
        <p className={cn('px-5 pb-5 text-sm', colors.textMuted)}>No active ranks yet.</p>
      ) : nationalities.length === 0 ? (
        <p className={cn('px-5 pb-5 text-sm', colors.textMuted)}>
          No nationalities in the crew pool yet. Add a nationality on a seafarer to open a column.
        </p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto px-5 pb-5">
          <table className="border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  className={cn(
                    'sticky left-0 top-0 z-30 min-w-[11rem] border-b border-r px-3 py-2 text-left text-xs font-semibold',
                    colors.headerBg,
                    colors.border,
                    colors.text,
                  )}
                >
                  Rank
                </th>
                {nationalities.map((nationality) => (
                  <th
                    key={nationality.id}
                    colSpan={MONTH_FIELDS.length}
                    className={cn(
                      'sticky top-0 z-20 h-[3.25rem] border-b border-l px-2 text-center text-xs font-semibold',
                      colors.headerBg,
                      colors.border,
                      colors.text,
                    )}
                  >
                    <div>{nationality.name}</div>
                    {nationality.code ? (
                      <div className={cn('text-[10px] font-normal', colors.textMuted)}>{nationality.code}</div>
                    ) : null}
                  </th>
                ))}
              </tr>
              <tr>
                {nationalities.map((nationality) =>
                  MONTH_FIELDS.map((field, index) => (
                    <th
                      key={`${nationality.id}-${field.key}`}
                      title={field.fullName}
                      className={cn(
                        'group relative sticky top-[3.25rem] z-20 w-[5.5rem] whitespace-nowrap border-b px-1 py-1.5 text-center text-[10px] font-semibold leading-tight',
                        index === 0 && 'border-l',
                        colors.headerBg,
                        colors.border,
                        colors.textMuted,
                      )}
                    >
                      {field.label}
                      <span
                        role="tooltip"
                        className={cn(
                          'pointer-events-none absolute left-1/2 top-full z-40 mt-1 hidden -translate-x-1/2 whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-medium normal-case shadow-md group-hover:block',
                          colors.surface,
                          colors.border,
                          colors.text,
                        )}
                      >
                        {field.fullName}
                      </span>
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {ranks.map((rank) => (
                <tr key={rank.id}>
                  <th
                    scope="row"
                    className={cn(
                      'sticky left-0 z-10 border-b border-r px-3 py-1.5 text-left font-medium',
                      colors.surface,
                      colors.border,
                      colors.text,
                    )}
                  >
                    <div className="truncate">{rank.name}</div>
                    {rank.abrv ? (
                      <div className={cn('text-[10px] font-normal', colors.textMuted)}>{rank.abrv}</div>
                    ) : null}
                  </th>
                  {nationalities.map((nationality) => {
                    const key = cellKey(rank.id, nationality.id);
                    const busy = pending.has(key);
                    return MONTH_FIELDS.map((field, index) => (
                      <td
                        key={`${key}-${field.key}`}
                        className={cn(
                          'border-b px-1 py-1',
                          index === 0 && 'border-l',
                          colors.border,
                          isDark ? 'bg-transparent' : 'bg-white',
                        )}
                      >
                        <input
                          aria-label={`${rank.name} ${nationality.name} ${field.fullName}`}
                          title={field.fullName}
                          inputMode="numeric"
                          value={displayValue(rank.id, nationality.id, field.key)}
                          disabled={!canEdit || busy}
                          onChange={(event) =>
                            onChange(rank.id, nationality.id, field.key, event.target.value)
                          }
                          onBlur={() => {
                            void saveCell(rank.id, nationality.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.currentTarget.blur();
                            }
                          }}
                          className={cn(
                            'h-7 w-full rounded border bg-transparent px-1 text-center text-xs tabular-nums outline-none focus:ring-1 focus:ring-[#3d5a80] disabled:opacity-60',
                            colors.border,
                            colors.text,
                          )}
                        />
                      </td>
                    ));
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContractTermsPanel;
